/**
 * Shared vocabulary for the primitive library.
 *
 * Non-component exports live here rather than beside the components that use
 * them: a module mixing component and non-component exports defeats React Fast
 * Refresh (react-refresh/only-export-components).
 */

/**
 * Service / execution health. A small fixed scale with reserved meaning —
 * never reused as a chart series colour, and always rendered with an icon and a
 * label so meaning never rests on colour alone.
 */
export type StatusRole = 'good' | 'warning' | 'serious' | 'critical'

/**
 * Signal severity. Deliberately a DIFFERENT scale from {@link StatusRole}:
 * service health and signal severity appear side by side on Overview, and
 * conflating them is the failure mode. "low" is neutral ink — "14 reports unused
 * for 180 days" is not a health state.
 */
export type SeverityLevel = 'critical' | 'high' | 'medium' | 'low'

/** ETL stage / pipeline node state. */
export type StageState = 'pending' | 'active' | 'done' | 'warning' | 'failed' | 'skipped'

/**
 * Which direction is *good* for a metric.
 *
 * This exists because tone cannot be derived from the sign alone: "failed
 * records up 2.1%" is bad and "records processed up 12.5%" is good. Callers
 * declare intent; {@link deltaTone} derives the tone.
 */
export type DeltaPolarity = 'higher-is-better' | 'lower-is-better'

/** Resolved tone for a delta, mapping to the --delta-* tokens. */
export type DeltaTone = 'favorable' | 'adverse' | 'neutral'

/**
 * Derives a delta's tone from its sign and the metric's polarity.
 *
 * The arrow direction should still follow the raw sign — only the COLOUR
 * follows intent. A rise in failures points up and reads adverse.
 */
export function deltaTone(change: number, polarity: DeltaPolarity): DeltaTone {
  if (change === 0 || Number.isNaN(change)) return 'neutral'
  const isUp = change > 0
  const goodWhenUp = polarity === 'higher-is-better'
  return isUp === goodWhenUp ? 'favorable' : 'adverse'
}

/** A DataTable column. Kept here so the component module exports only components. */
export interface DataTableColumn<T> {
  key: string
  header: import('react').ReactNode
  render: (row: T) => import('react').ReactNode
  /** Right-align and use tabular figures — for columns of numbers. */
  numeric?: boolean
  width?: number | string
}

/** Human labels for the status scale. Used as the required text beside the icon. */
export const STATUS_LABELS: Record<StatusRole, string> = {
  good: 'Healthy',
  warning: 'Warning',
  serious: 'Elevated',
  critical: 'Critical',
}

/** Human labels for the severity scale. */
export const SEVERITY_LABELS: Record<SeverityLevel, string> = {
  critical: 'Critical',
  high: 'High',
  medium: 'Medium',
  low: 'Low',
}
